import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import cx from 'classnames';
import {Link} from 'components/DSYS/Link';
import {StatusButton} from 'components/DSYS/StatusButton';
import {type Column, Table} from 'components/DSYS/Table';
import {DependencyStatusScores} from 'components/workstreams/DependencyStatusScores';
import {format} from 'date-fns';
import {useMemo} from 'react';
import {STATUS_SEMANTIC_ORDER, type StatusValue} from 'views/workstreams/detail/types';
import styles from 'views/workstreams/detail/WorkstreamTable.module.css';
import {useWorkstream} from 'views/workstreams/hooks';
import {getFullWorkstreamMetrics, getWorkstreamUpdateVisualStatus} from 'views/workstreams/utils';

import {ClampedUpdate} from './ClampedUpdate';
import {PersonLink} from './PersonLink';
import {ProgressRow} from './ProgressRow';
import {ScanPlaceOrigin, ScanSectionOrigin} from './scanCardOrigin';
import {buildScanProgress, type ProgressItem} from './scanProgressItems';
import type {UpdateOpenedFrom, WorkstreamsViewPlace} from './workstreamsAnalytics';
import {isArchived, isRestricted, ownerOf, plain, type ScanWorkstream} from './WorkstreamScanList';
import {StatusFilterDropdown} from './WorkstreamsControls';
import {workstreamsPath} from './workstreamsRoutes';

type TableWorkstream = ScanWorkstream & {description?: string | null};

const statusRank = (workstream: ScanWorkstream) => {
  // Worst first, then closed, then archived.
  const index = STATUS_SEMANTIC_ORDER.indexOf(
    (workstream.updates?.[0]?.status ?? 'no_status') as StatusValue
  );
  return index < 0 ? STATUS_SEMANTIC_ORDER.length : index;
};
const byName = (a: ScanWorkstream, b: ScanWorkstream) => a.name.localeCompare(b.name);

// The old table's columns, widths and labels; Update takes the spare width.
const COLUMNS: Column<TableWorkstream>[] = [
  {type: 'workstream', minWidth: 200, width: 240, cantDisable: true, sort: byName},
  {
    type: 'update',
    minWidth: 520,
    spanner: true,
    sort: (a, b) => statusRank(a) - statusRank(b) || byName(a, b),
  },
  {type: 'metrics', minWidth: 316},
  {type: 'outcomes', minWidth: 300},
];
const COLUMN_LABELS = {
  workstream: 'column_workstreams',
  update: 'column_update',
  metrics: 'column_metrics',
  outcomes: 'milestones',
};
// Rows per Metrics / Milestones cell; the full page lists the rest.
const MAX_PROGRESS_ROWS = 3;
// The update clamps here (Show full update has the rest).
const UPDATE_LINES = 8;

const detailHref = (workstream: ScanWorkstream) => workstreamsPath(workstream.id, 'detail');

// Name, description, the sub-workstreams' status dots, then the DRI.
const WorkstreamCell = ({workstream}: {workstream: TableWorkstream}) => {
  const owner = ownerOf(workstream);
  const description = plain(workstream.description);
  // List rows carry only a count; the dots need the sub-workstreams themselves.
  const hasSubs = (workstream.dependencies_count ?? 0) > 0;
  const {workstream: full} = useWorkstream(hasSubs ? workstream.id : undefined);
  const subs = useMemo(
    () => (full?.dependencies ?? []).filter(({dependent}) => !isArchived(dependent)),
    [full?.dependencies]
  );
  return (
    <Stack gap="4" className={styles.workstreamNameCell}>
      {/* The page opens the full page on a plain click. */}
      <Text isBold>
        <Link to={detailHref(workstream)} hasNoUnderline showUnderlineOnHover monochromatic>
          <Truncate lines={2}>{workstream.name}</Truncate>
        </Link>
      </Text>
      {description && (
        <Text size="small" color="subtle">
          <Truncate>{description}</Truncate>
        </Text>
      )}
      {subs.length > 0 && <DependencyStatusScores dependencies={subs} />}
      {owner && <PersonLink person={owner} />}
    </Stack>
  );
};

// The status (opens the update modal) and date, then the update itself.
const UpdateCell = ({
  workstream,
  onLeaveUpdate,
}: {
  workstream: ScanWorkstream;
  onLeaveUpdate: (id: number, from?: UpdateOpenedFrom) => void;
}) => {
  const update = workstream.updates?.[0];
  // Nothing to show for one the viewer can't read, not even a status.
  if (isRestricted(workstream)) return null;
  return (
    <Stack gap="8">
      <Box display="flex" alignItems="center" style={{gap: 8}}>
        <StatusButton
          status={getWorkstreamUpdateVisualStatus(update?.status ?? 'no_status')}
          size="small"
          includeArchived
          onClick={(event: React.SyntheticEvent) => {
            event.preventDefault();
            onLeaveUpdate(workstream.id, {button: 'card_status', section: 'table'});
          }}
        />
        {update && (
          <Text size="small" color="faint">
            {format(new Date(update.created_at), 'MMM d')}
          </Text>
        )}
      </Box>
      {update ? (
        <ClampedUpdate update={update} lines={UPDATE_LINES} />
      ) : (
        <Text size="small" color="faint">
          No updates yet
        </Text>
      )}
    </Stack>
  );
};

// The first few (most urgent; Metrics leads with the north star), then a link
// to the full page for the rest.
const ProgressCell = ({workstream, items}: {workstream: ScanWorkstream; items: ProgressItem[]}) =>
  items.length === 0 ? (
    <Text size="small" color="faint">
      –
    </Text>
  ) : (
    <Stack gap="12">
      {items.slice(0, MAX_PROGRESS_ROWS).map((item) => (
        <ProgressRow key={item.key} item={item} />
      ))}
      {items.length > MAX_PROGRESS_ROWS && (
        <Text size="small" color="subtle">
          <Link to={detailHref(workstream)} showUnderlineOnHover monochromatic>
            +{items.length - MAX_PROGRESS_ROWS} more
          </Link>
        </Text>
      )}
    </Stack>
  );

const TableRow = ({
  workstream,
  onLeaveUpdate,
}: {
  workstream: TableWorkstream;
  onLeaveUpdate: (id: number, from?: UpdateOpenedFrom) => void;
}) => {
  const progress = useMemo(
    () =>
      buildScanProgress({
        workstreamId: workstream.id,
        metrics: getFullWorkstreamMetrics(workstream.metrics),
        // List rows carry no Jira initiatives; the full page shows them.
        jiraObjects: [],
        isCore: false,
      }),
    [workstream.id, workstream.metrics]
  );
  const metrics = progress.pinned ? [progress.pinned, ...progress.metrics] : progress.metrics;

  return (
    <Table.Row>
      <Table.Cell>
        <WorkstreamCell workstream={workstream} />
      </Table.Cell>
      <Table.Cell>
        <UpdateCell workstream={workstream} onLeaveUpdate={onLeaveUpdate} />
      </Table.Cell>
      <Table.Cell>
        <ProgressCell workstream={workstream} items={metrics} />
      </Table.Cell>
      <Table.Cell>
        <ProgressCell workstream={workstream} items={progress.milestones} />
      </Table.Cell>
    </Table.Row>
  );
};

/**
 * /workstreams `?table=1`: the Execution view as the old
 * /workstreams table, for those who'd rather scan rows than cards. Same frame
 * and columns, built from v4 pieces and read-only: the status opens the update
 * modal and the title the full page, where everything else edits. Sorts by
 * name (to start) or status. `embedded`: inside a page's section (a detail
 * page's Sub-workstreams), flush with it and growing with its rows.
 */
export const WorkstreamListTable = ({
  workstreams,
  loading,
  place,
  embedded = false,
  keepOrder = false,
  onLeaveUpdate,
  filter,
}: {
  workstreams: TableWorkstream[];
  loading: boolean;
  // The page listing these, sent with its links' events.
  place?: WorkstreamsViewPlace;
  embedded?: boolean;
  // Start unsorted (sub-workstreams in their saved order).
  keepOrder?: boolean;
  onLeaveUpdate: (id: number, from?: UpdateOpenedFrom) => void;
  // The Update column's status filter, when the rows come from a filtered query.
  filter?: {
    statusFilter: StatusValue[];
    includeArchived: boolean;
    onStatusFilterChange: (next: StatusValue[]) => void;
    onIncludeArchivedChange: (next: boolean) => void;
  };
}) => {
  const headerContent = filter && {
    update: (
      <Box display="flex" justifyContent="flex-end" style={{width: '100%', paddingRight: 2}}>
        <StatusFilterDropdown
          selected={filter.statusFilter}
          includeArchived={filter.includeArchived}
          onChange={filter.onStatusFilterChange}
          onArchivedChange={filter.onIncludeArchivedChange}
        />
      </Box>
    ),
  };
  const table = (
    <Table
      columns={COLUMNS}
      data={workstreams}
      sortConfig={keepOrder ? undefined : {columnId: 'workstream', asc: true}}
      bodyCellPadding={8}
      headerTextSize="small"
      columnLabelOverrides={COLUMN_LABELS}
      columnHeaderContent={headerContent}
      className={cx(styles.table, styles.tableRedesign, {
        [styles.tableRedesignFlush]: embedded,
        [styles.tableRedesignSubworkstreams]: embedded,
      })}
    >
      <Table.Header />
      <Table.Body>
        {loading ? (
          <Table.Loading />
        ) : workstreams.length === 0 ? (
          <Table.Row>
            <Table.Cell colSpan={COLUMNS.length}>
              <Text size="small" color="subtle">
                No workstreams here.
              </Text>
            </Table.Cell>
          </Table.Row>
        ) : (
          <Table.Rows
            renderRow={(workstream: TableWorkstream) => (
              <TableRow workstream={workstream} onLeaveUpdate={onLeaveUpdate} />
            )}
          />
        )}
      </Table.Body>
    </Table>
  );

  return (
    <ScanPlaceOrigin place={place}>
      <ScanSectionOrigin section={embedded ? 'subworkstreams' : 'table'}>
        {embedded ? (
          table
        ) : (
          // The page's pane: the table fills it and scrolls itself.
          <div className={styles.tableLayout}>
            <div className={styles.tableScrollArea}>{table}</div>
          </div>
        )}
      </ScanSectionOrigin>
    </ScanPlaceOrigin>
  );
};
